import { useMemo } from 'react';
import { themeVars } from '@dreamtree-org/landing-kit/schema';
import { EmptyState } from '@/components';
import { themePaletteVars } from '@/theme/colorChannels';
import { useQuery } from '@/lib/useQuery';
import { SORTS, parseFilters } from '../lib/filters';
import { effectiveSort, templateOf } from '../lib/design';
import { StorefrontPreviewProvider, useStorefront } from '../StorefrontContext';
import { StoreHeader, StoreFooter } from '../components/StoreLayout';
import Region from './Region';
import { ListingCtx, LISTING_SLOT_COMPONENTS, buildListing, previewResult } from './listing';
import { ProductCtx, PRODUCT_SLOT_COMPONENTS, buildProduct } from './product';
import { asVariant, findVariant, initialSelection } from '../lib/variants';

/** An inspection report shaped like the real one, so the product preview shows every block. */
const SAMPLE_INSPECTION = {
  report_id: 'SAMPLE',
  version: 1,
  inspected_at: '2026-10-01T10:00:00.000Z',
  inspector: 'your technician',
  workshop: 'your workshop',
  passed: 38,
  total: 40,
  failed: 2,
  groups: [
    { category: 'Display', items: [{ name: 'Dead pixels', result: 'pass' }, { name: 'Backlight even', result: 'pass' }] },
    { category: 'Keyboard and touchpad', items: [{ name: 'Every key', result: 'pass' }, { name: 'Touchpad click', result: 'fail', note: 'Light click, works' }] },
  ],
  battery: { health_pct: 91, measured_at: '2026-10-01T10:00:00.000Z', method: 'Battery report', cycles: 212 },
  accessories: [{ name: 'Charger', detail: 'Original', included: true }, { name: 'Box', detail: 'Not the original', included: false }],
};

const noop = () => {};

function ListingPreview() {
  const sf = useStorefront();
  const tpl = templateOf(sf.doc, 'listing');
  const f = { ...parseFilters({}, null), sort: effectiveSort('', tpl.settings.default_sort, SORTS) };
  const ctx = buildListing(sf, { type: null, f, settings: tpl.settings, apply: noop, openFilters: noop, result: previewResult(sf, { f, settings: tpl.settings }) });
  return (
    <ListingCtx.Provider value={ctx}>
      <div className="space-y-5">
        <Region blocks={tpl.blocks} slots={LISTING_SLOT_COMPONENTS} className="-mx-4 sm:-mx-6" />
      </div>
    </ListingCtx.Provider>
  );
}

function ProductPreview() {
  const sf = useStorefront();
  const seller = useQuery('sfSeller', { slug: sf.seller.slug, name: sf.seller.name }, { select: (r) => r.data });
  const l = sf.listings[0];
  if (!l) return <EmptyState title="No products to show yet">Publish a listing and the product page preview fills in with it.</EmptyState>;
  const tpl = templateOf(sf.doc, 'product');
  // A listing with options previews its first variant on sale (MERCH-87).
  const sel = initialSelection(l);
  const variant = findVariant(l, sel);
  const ctx = buildProduct(sf, { l: asVariant(l, variant), tpl, insp: { data: SAMPLE_INSPECTION, loading: false, error: null }, seller, variant, sel });
  return (
    <ProductCtx.Provider value={ctx}>
      <div className={ctx.layout.grid}>
        <Region blocks={tpl.blocks} slots={PRODUCT_SLOT_COMPONENTS} className={ctx.layout.full} />
      </div>
    </ProductCtx.Provider>
  );
}

/** Which storefront page each Studio area previews (a registry; home has its own TenantPage preview). */
const PREVIEW_PAGES = { listing: ListingPreview, product: ProductPreview, card: ListingPreview, chrome: ListingPreview };

/**
 * Storefront Studio's live preview: the draft document rendered by the real
 * storefront components (header, the page's template blocks, footer) over the
 * store's own listings, coloured by the draft theme. Look, don't touch: links
 * and buttons are inert here; "Preview" opens the real storefront.
 */
export default function StorePreview({ area, doc, theme, listings, types, categories, name, mediaUrl }) {
  const vars = useMemo(() => {
    const v = themeVars(theme);
    return { style: { ...themePaletteVars(v.palette), '--radius': v.style['--dt-lp-radius'], fontFamily: v.font.body }, href: v.font.href };
  }, [theme]);
  const Page = PREVIEW_PAGES[area] || ListingPreview;
  return (
    <StorefrontPreviewProvider doc={doc} theme={theme} listings={listings} types={types} categories={categories} name={name} mediaUrl={mediaUrl}>
      {vars.href ? <link rel="stylesheet" href={vars.href} /> : null}
      <div className="pointer-events-none select-none bg-[var(--app-bg)] text-[var(--app-text)]" style={vars.style}>
        <StoreHeader />
        <main className="mx-auto w-full max-w-screen-xl px-4 py-4 sm:px-6 sm:py-6">
          <Page />
        </main>
        <StoreFooter />
      </div>
    </StorefrontPreviewProvider>
  );
}
